import { cn } from '@ai-tutor/ui';

import type { BillingInterval } from '@/lib/billing';

interface BillingIntervalToggleProps {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  /** Optional "Save N%" badge shown on the Yearly segment. */
  savingsPct?: number | null;
  disabled?: boolean;
}

/**
 * Segmented Monthly / Yearly control for the single paid plan, rendered by
 * `PlanPicker` wherever a plan is chosen.
 *
 * "Yearly", not "Annual": the plans it switches between are named
 * "Plus · Monthly" / "Plus · Yearly" everywhere else (`planDisplayName`), and a
 * toggle that said one word above a button that said another read as two
 * different things.
 */
export function BillingIntervalToggle({
  value,
  onChange,
  savingsPct,
  disabled = false,
}: BillingIntervalToggleProps) {
  // A solid white chip, not `bg-white/20 text-white`: at 10px the translucent
  // version composited to 3.51:1 against the brand segment and failed WCAG AA
  // (axe catches it in the PricingStep stories). Opaque white with brand text
  // keeps the badge legible at that size.
  const activeBadge =
    'bg-white text-brand-700 dark:bg-brand-950/30 dark:text-brand-950';
  const inactiveBadge =
    'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300';
  return (
    <div className='inline-flex items-center rounded-full border border-border bg-muted p-1'>
      {(['month', 'year'] as const).map(interval => {
        const active = value === interval;
        const label = interval === 'month' ? 'Monthly' : 'Yearly';
        return (
          <button
            key={interval}
            type='button'
            disabled={disabled}
            onClick={() => onChange(interval)}
            aria-pressed={active}
            className={cn(
              // `tap-target`: drawn 32-36px tall, tapped as 44 on a phone.
              'tap-target inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-60',
              active
                ? 'bg-brand-600 text-white shadow-sm dark:bg-brand-400 dark:text-brand-950'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {label}
            {interval === 'year' && savingsPct != null && savingsPct > 0 && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 font-mono text-eyebrow',
                  active ? activeBadge : inactiveBadge
                )}
              >
                Save {savingsPct}%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
